/**
 * V4 scenario dictionaries cache. Fetched once at session start from
 * `/v1/scenarios/{triggers,actions}` and consumed by `toV4ScenarioRequest` and
 * `isV3TriggerEditableInV4`, so the picker and the save agree on what V4 takes.
 */

import V4API from '../../api/V4client';

let triggerDefs = [];
let actionDefs = [];
let loaded = false;

function flatten(categories) {
    if (!Array.isArray(categories)) return [];
    const out = [];
    for (const cat of categories) {
        for (const item of cat?.items || []) {
            if (item?.value) out.push(item);
        }
    }
    return out;
}

export function setV4TriggerDefs(categoriesOrItems) {
    if (Array.isArray(categoriesOrItems) && categoriesOrItems.length && categoriesOrItems[0]?.items !== undefined) {
        triggerDefs = flatten(categoriesOrItems);
    } else if (Array.isArray(categoriesOrItems)) {
        triggerDefs = categoriesOrItems.filter(d => d?.value);
    } else {
        triggerDefs = [];
    }
}

export function setV4ActionDefs(categoriesOrItems) {
    if (Array.isArray(categoriesOrItems) && categoriesOrItems.length && categoriesOrItems[0]?.items !== undefined) {
        actionDefs = flatten(categoriesOrItems);
    } else if (Array.isArray(categoriesOrItems)) {
        actionDefs = categoriesOrItems.filter(d => d?.value);
    } else {
        actionDefs = [];
    }
}

export function getV4TriggerSources() {
    return triggerDefs.map(d => d.value);
}

export function getV4ActionTypes() {
    return actionDefs.map(d => d.value);
}

export function isV4DictionaryLoaded() {
    return loaded;
}

// Permissive when dict isn't loaded — V4 backend will validate.
export function isKnownV4TriggerSource(source) {
    if (!loaded || !triggerDefs.length) return true;
    return triggerDefs.some(d => d.value === source);
}

export function isKnownV4ActionType(type) {
    if (!loaded || !actionDefs.length) return true;
    return actionDefs.some(d => d.value === type);
}

export async function fetchV4ScenarioDictionaries(scope) {
    const summarize = (label, p) =>
        p.then(
            res => ({ label, ok: true, status: res?.status, data: res?.data }),
            err => ({
                label,
                ok: false,
                status: err?.response?.status,
                error: err?.response?.data || err?.message || String(err),
            })
        );

    const triggersCall = V4API.scenariosTriggersList(scope);
    const actionsCall = V4API.scenariosActionsList(scope);
    const [tInfo, aInfo] = await Promise.all([
        summarize('triggers', triggersCall),
        summarize('actions', actionsCall),
    ]);

    setV4TriggerDefs(tInfo.ok ? (tInfo.data || []) : []);
    setV4ActionDefs(aInfo.ok ? (aInfo.data || []) : []);
    loaded = tInfo.ok || aInfo.ok;

    // eslint-disable-next-line no-console
    console.info('[V4 scenario dictionaries]', {
        triggerSources: getV4TriggerSources(),
        actionTypes: getV4ActionTypes(),
        triggersFetch: tInfo,
        actionsFetch: aInfo,
    });

    return loaded;
}
